import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Button,
  Group,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { RegistrationLayout } from './RegistrationLayout';
import { PasswordMeter, PasswordRules } from '../../shared/components/PasswordRules';
import { PASSWORD_MIN_LENGTH } from '../../shared/password/policy';
import { ApiError } from '../../shared/api/problem';
import {
  completeRegistration,
  getRegistrationByToken,
} from './api';

export function CompleteRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams({ strict: false }) as { token: string };

  const tokenQuery = useQuery({
    queryKey: ['registration', 'verify', token],
    queryFn: ({ signal }) => getRegistrationByToken(token, signal),
    retry: false,
  });

  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => completeRegistration(token, password),
    onSuccess: () => {
      // Cookie is now set by the server. Land on a stub home route — when
      // the dashboard is built (M3.10+) this becomes /app or similar.
      void navigate({ to: '/' });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 404) { setSubmitError(t('registration.complete.errors.tokenNotFound')); return; }
        if (err.status === 410) { setSubmitError(t('registration.complete.errors.tokenExpired')); return; }
        if (err.status === 409) { setSubmitError(t('registration.complete.errors.tokenAlreadyUsed')); return; }
      }
      setSubmitError(t('registration.complete.errors.generic'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setFieldError(null);
    if (!password) { setFieldError(t('registration.complete.errors.passwordRequired')); return; }
    if (password.length < PASSWORD_MIN_LENGTH) { setFieldError(t('registration.complete.errors.passwordTooShort')); return; }
    mutation.mutate();
  }

  // ----- error views ---------------------------------------------------

  if (tokenQuery.isLoading) {
    return (
      <RegistrationLayout step="step3Of3">
        <Stack>
          <Text size="sm" c="dimmed">{t('shell.loading')}</Text>
        </Stack>
      </RegistrationLayout>
    );
  }

  if (tokenQuery.isError) {
    const err = tokenQuery.error;
    const status = err instanceof ApiError ? err.status : 0;
    const messageKey =
      status === 410 ? 'registration.verifyError.expired'
      : status === 409 ? 'registration.verifyError.consumed'
      : 'registration.verifyError.notFound';
    return (
      <RegistrationLayout step="step3Of3">
        <Stack>
          <Title order={2}>{t('registration.verifyError.title')}</Title>
          <Text size="sm" c="dimmed">{t(messageKey)}</Text>
          <Button color="invetrak" onClick={() => void navigate({ to: '/register' })}>
            {t('registration.verifyError.startOver')}
          </Button>
        </Stack>
      </RegistrationLayout>
    );
  }

  // ----- happy path: set password -------------------------------------

  const reg = tokenQuery.data!;

  return (
    <RegistrationLayout step="step3Of3">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>{t('registration.complete.title')}</Title>
          <Text size="sm" c="dimmed">
            {t('registration.complete.welcome', {
              firstName: reg.contactFirstName,
              businessName: reg.businessName,
            })}
          </Text>
        </Stack>

        <form onSubmit={onSubmit} noValidate>
          <Stack gap="md">
            <PasswordInput
              label={t('registration.complete.password')}
              placeholder={t('registration.complete.passwordHint')}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              error={fieldError}
              required
              autoComplete="new-password"
            />

            <PasswordMeter password={password} />
            <PasswordRules password={password} />

            <Alert
              icon={<IconInfoCircle size={16} />}
              color="invetrak"
              variant="light"
            >
              {t('registration.complete.provisioningHint')}
            </Alert>

            {submitError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {submitError}
              </Alert>
            )}

            <Button
              type="submit"
              color="invetrak"
              size="md"
              fullWidth
              loading={mutation.isPending}
            >
              {mutation.isPending
                ? t('registration.complete.submitting')
                : t('registration.complete.submit')}
            </Button>
          </Stack>
        </form>

        <Group justify="center" gap={6}>
          <Anchor size="sm" href="#" c="dimmed" onClick={(e) => { e.preventDefault(); void navigate({ to: '/register' }); }}>
            {t('registration.verifyError.startOver')}
          </Anchor>
        </Group>
      </Stack>
    </RegistrationLayout>
  );
}
